import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FontText, PrimaryButton } from './';
import { localAnalytics } from '../../utils/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../api/initSupabase';
import { useTranslation } from 'react-i18next';

interface BookCardData {
  title: string;
  hook: string;
  slug?: string;
}

interface BookData {
  id: number;
  title: string;
  picture: string | null;
  slug: string;
  chapter_count: number;
  read_count: number;
  book_author: Array<{
    author: {
      id: number;
      name: string;
    };
  }>;
}

interface BookCardProps {
  book: BookCardData;
  source?: string;
}

export const BookCard: React.FC<BookCardProps> = ({ book, source = 'Hub' }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { t } = useTranslation();
  const [bookData, setBookData] = useState<BookData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookData = async () => {
      if (!book.slug) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('book')
          .select(
            `
            id,
            title,
            picture,
            slug,
            chapter_count,
            read_count,
            book_author!inner(author:author_id(id, name))
          `
          )
          .eq('slug', book.slug)
          .single();

        if (error) {
          console.error('Error fetching book data:', error);
        } else {
          setBookData(data as BookData);
        }
      } catch (error) {
        console.error('Error fetching book data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookData();
  }, [book.slug]);

  const handleBookClick = () => {
    localAnalytics().logEvent('HubBookCardClicked', {
      screen: source,
      action: 'BookCardClicked',
      userId,
      bookTitle: book.title,
    });

    if (book.slug) {
      navigate(`/books/${book.slug}`);
    }
  };

  const isLargeScreen = window.innerWidth > 900;

  const styles: Record<string, React.CSSProperties> = {
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      overflow: 'hidden',
      border: `1px solid ${theme.colors.overlay}`,
      cursor: book.slug ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      height: 'fit-content',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    },
    cardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
    },
    imageContainer: {
      width: '100%',
      height: isLargeScreen ? 250 : 200,
      overflow: 'hidden',
      position: 'relative',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'top',
    },
    placeholderImage: {
      width: '100%',
      height: '100%',
      backgroundColor: theme.colors.overlay,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      padding: 16,
    },
    title: {
      color: theme.colors.text,
      marginBottom: 8,
    },
    author: {
      color: theme.colors.textSecondary,
      marginBottom: 8,
    },
    hook: {
      color: theme.colors.textSecondary,
      lineHeight: '1.5',
      marginBottom: 16,
    },
    stats: {
      display: 'flex',
      gap: 8,
      marginBottom: 16,
      alignItems: 'center',
    },
    statsText: {
      color: theme.colors.textTertiary,
    },
    readButton: {
      width: '100%',
    },
    loadingContainer: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      height: isLargeScreen ? 400 : 350,
      border: `1px solid ${theme.colors.overlay}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <FontText small style={{ color: theme.colors.textSecondary }}>
          {t('loading')}
        </FontText>
      </div>
    );
  }

  return (
    <div
      style={styles.card}
      onClick={book.slug ? handleBookClick : undefined}
      onMouseEnter={e => {
        if (book.slug) {
          Object.assign(e.currentTarget.style, styles.cardHover);
        }
      }}
      onMouseLeave={e => {
        if (book.slug) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
        }
      }}
    >
      <div style={styles.imageContainer}>
        {bookData?.picture ? (
          <img src={bookData.picture} alt={book.title} style={styles.image} />
        ) : (
          <div style={styles.placeholderImage}>
            <FontText small style={{ color: theme.colors.textSecondary }}>
              {t('no_image')}
            </FontText>
          </div>
        )}
      </div>

      <div style={styles.content}>
        <FontText h4 style={styles.title}>
          {book.title}
        </FontText>

        {bookData?.book_author?.[0]?.author && (
          <FontText small style={styles.author}>
            {t('by_author', { author: bookData.book_author[0].author.name })}
          </FontText>
        )}

        <FontText normal style={styles.hook}>
          {book.hook}
        </FontText>

        {bookData && (
          <div style={styles.stats}>
            <FontText tiny style={styles.statsText}>
              {t('chapter_count', { number: bookData.chapter_count })}
            </FontText>
            <FontText tiny style={styles.statsText}>
              |
            </FontText>
            <FontText tiny style={styles.statsText}>
              {bookData.read_count.toLocaleString()} {t('reads')}
            </FontText>
          </div>
        )}

        {book.slug && (
          <div style={styles.readButton}>
            <PrimaryButton onClick={handleBookClick}>
              <FontText>{t('read_now')}</FontText>
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
};
